/**
 * Custom hook for salary conversion and display logic
 */

import { useMemo } from 'react';
import { convertSalaryToMonthlyEUR, hasOriginalSalary, type OriginalSalary } from '../utils/salaryConversion';
import type { DisplayJob } from '../../../../../types';

export interface UseSalaryDisplayOptions {
  displayJob: DisplayJob | null;
  showOriginalSalary: boolean;
}

export interface SalaryDisplayResult {
  originalSalary: OriginalSalary | null;
  convertedSalary: OriginalSalary | null;
  displaySalary: OriginalSalary | null;
  shouldShowOriginalButton: boolean;
}

/**
 * Calculate salary conversion and display options
 */
export function useSalaryDisplay({ displayJob, showOriginalSalary }: UseSalaryDisplayOptions): SalaryDisplayResult {
  return useMemo(() => {
    if (!displayJob) {
      return {
        originalSalary: null,
        convertedSalary: null,
        displaySalary: null,
        shouldShowOriginalButton: false,
      };
    }

    const { salaryRangeMin, salaryRangeMax, currency, salaryPeriod } = displayJob;

    // Convert salary to EUR/month and store original values
    const originalSalary: OriginalSalary | null =
      (typeof salaryRangeMin === 'number' && typeof salaryRangeMax === 'number' && salaryRangeMin > 0 && salaryRangeMax > 0)
        ? {
            min: salaryRangeMin,
            max: salaryRangeMax,
            currency: currency || 'EUR',
            period: salaryPeriod || 'monthly',
          }
        : null;

    const convertedSalary = originalSalary
      ? convertSalaryToMonthlyEUR(originalSalary.min, originalSalary.max, originalSalary.currency, originalSalary.period)
      : null;

    const displaySalary = showOriginalSalary && originalSalary ? originalSalary : (convertedSalary || originalSalary);
    const shouldShowOriginalButton = originalSalary && hasOriginalSalary(originalSalary.currency, originalSalary.period);

    return {
      originalSalary,
      convertedSalary,
      displaySalary,
      shouldShowOriginalButton,
    };
  }, [displayJob, showOriginalSalary]);
}
